// Visor de un archivo dentro de /almacenamiento (round 2026-10-28): imagen a tamaño de pantalla o
// video con <VideoPlayer>. Los PDF y los documentos de Office no pasan por acá (se abren en una
// pestaña o se descargan: ver aperturaArchivo.ts).
//
// La página lo monta SOLO mientras hay un archivo abierto, así que al cerrar el modal el
// reproductor se desmonta y el video deja de bajar datos. Abrir cuenta como una lectura normal
// de /files: la URL completa se pide recién ahora, nunca por un hover.
//
// Video: SIEMPRE <VideoPlayer> (regla del proyecto). Abre en la versión liviana (`previewUrl`)
// o en HD según la preferencia de la persona, con el botón Baja / HD del propio reproductor.
import React, { useState } from "react";
import { Alert, Button, Modal, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import VideoPlayer from "../../../Components/VideoPlayer";
import VisorImagen from "../../../Components/VisorImagen";
import type { Archivo } from "../../../services/almacenamiento.api";
import { urlDeDescarga } from "../almacenamientoUtils";

const { Text } = Typography;

interface Props {
  archivo: Archivo;
  onClose: () => void;
}

const VisorArchivoModal: React.FC<Props> = ({ archivo, onClose }) => {
  const [fallo, setFallo] = useState(false);
  const url = archivo.url as string;
  const esVideo = archivo.tipo === "video";

  return (
    <Modal
      open
      // El video va anclado ARRIBA, no centrado: su escenario reserva el tamaño de antemano y, en un modal
      // centrado, cualquier ajuste de alto lo movería (ver Components/VideoPlayer.tsx).
      centered={!esVideo}
      style={esVideo ? { top: 12 } : undefined}
      className="alm-visor"
      title={<span data-testid="alm-visor-titulo">{archivo.nombre}</span>}
      width="min(96vw, 1100px)"
      onCancel={onClose}
      footer={[
        <Button
          key="descargar"
          icon={<DownloadOutlined />}
          href={urlDeDescarga(url)}
          download={archivo.nombre}
          target="_blank"
          rel="noopener noreferrer"
        >
          {esVideo ? "Descargar original" : "Descargar"}
        </Button>,
        <Button key="cerrar" type="primary" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
    >
      {fallo ? (
        <Alert
          type="error"
          showIcon
          message={esVideo ? "No se pudo reproducir el video" : "No se pudo cargar la imagen"}
          description="Puede que el archivo ya no esté en el disco o que tu sesión haya vencido."
        />
      ) : esVideo ? (
        <VideoPlayer
          src={url}
          previewSrc={archivo.previewUrl || null}
          poster={archivo.thumbUrl || undefined}
          className="alm-visor-video"
          wrapperClassName="alm-visor-video-envoltorio"
          nombreArchivo={archivo.nombre}
          altoMaximo="calc(100dvh - 290px)"
          // El pie del modal ya trae «Descargar original».
          descargable={false}
          autoPlay
          playsInline
          preload="metadata"
          aria-label={`Video ${archivo.nombre}`}
          testId="alm-visor-video"
          onError={() => setFallo(true)}
        />
      ) : (
        <VisorImagen
          className="alm-visor-img"
          src={url}
          alt={archivo.nombre}
          testId="alm-visor-img"
          altura="calc(100dvh - 290px)"
          onError={() => setFallo(true)}
        />
      )}
      {esVideo && !fallo && archivo.previewUrl && (
        <Text type="secondary" className="alm-visor-nota">
          Se abre en la versión liviana si existe (gasta menos datos); el selector «Baja | HD» bajo
          el video pasa a HD.
        </Text>
      )}
    </Modal>
  );
};

export default VisorArchivoModal;
